import { useEffect, useRef, useState } from "react";
import { post, type ThumbGrants } from "./api.ts";

/**
 * Read URLs for a screenful of tiles, in as few requests as possible, kept alive while the tab
 * stays open.
 *
 * A grant is short-lived, so a library left open for a while would quietly turn into a grid of
 * broken images. Each batch schedules its own refresh a minute before the earliest expiry, and the
 * old URL keeps serving until the new one lands, so a tile never blanks. A recording that has no
 * rendition yet is remembered as absent rather than asked for over and over.
 */
const BATCH = 200;
const REFRESH_BEFORE_MS = 60_000;
const RETRY_MS = 10_000;

export function useThumbs(tripId: string, assetIds: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const held = useRef(new Map<string, { url: string; expiresAt: number }>());
  const absent = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const key = assetIds.join(",");

  useEffect(() => {
    let live = true;
    const ids = key === "" ? [] : key.split(",");

    const due = (now: number) =>
      ids.filter((id) => {
        if (absent.current.has(id)) return false;
        const h = held.current.get(id);
        return h === undefined || h.expiresAt - now < REFRESH_BEFORE_MS;
      });

    const run = async () => {
      const now = Date.now();
      const need = due(now);
      if (need.length === 0) return schedule();
      for (let i = 0; i < need.length; i += BATCH) {
        const slice = need.slice(i, i + BATCH);
        try {
          const r = await post<ThumbGrants>(`/v1/trips/${tripId}/media-grants/batch`, {
            variant: "THUMB",
            assetIds: slice,
          });
          if (!live) return;
          for (const g of r.items) {
            held.current.set(g.assetId, { url: g.url, expiresAt: Date.parse(g.expiresAt) });
          }
          for (const id of r.missing) absent.current.add(id);
        } catch {
          // A failed batch leaves the previous URLs in place; the next pass tries again.
          if (!live) return;
        }
      }
      setUrls(Object.fromEntries([...held.current].map(([id, h]) => [id, h.url])));
      schedule();
    };

    const schedule = () => {
      if (timer.current !== null) clearTimeout(timer.current);
      const now = Date.now();
      const soonest = ids
        .map((id) => held.current.get(id)?.expiresAt)
        .filter((e): e is number => e !== undefined)
        .reduce<number | null>((min, e) => (min === null || e < min ? e : min), null);
      // Anything still without a URL means a batch failed: come back for it soon. Without this a
      // single blip on first load would leave the grid blank until the tab was rebuilt, because
      // there would be no expiry to schedule the next pass from.
      const retrying = due(now).length > 0;
      const refreshIn = soonest === null ? null : soonest - REFRESH_BEFORE_MS - now;
      const delay = retrying ? Math.min(RETRY_MS, refreshIn ?? RETRY_MS) : refreshIn;
      if (delay === null) return;
      timer.current = setTimeout(() => void run(), Math.max(1000, delay));
    };

    void run();
    return () => {
      live = false;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [tripId, key]);

  return urls;
}
